// Choropleth boundary access for the Map visual.
//
// Shapes come from `/geo` — served by the platform backend from
// backend/app/static/geo (see services/geo_boundaries.py) and by the
// standalone runtime from the copies its bundles ship. Both hosts use the SAME
// absolute paths, so nothing here needs per-host configuration:
//
//   /geo/index.json             alias -> feature-id lookups
//   /geo/admin0/world.geojson   country outlines (id = ISO A2)
//   /geo/admin1/<CC>.geojson    one country's states (id = ISO 3166-2)
//
// Those files are GENERATED, not shipped, so a country nobody has downloaded
// yet 404s. `loadBoundaries` then asks the backend to fetch it
// (POST /api/geo/download) and polls until it lands — the "automatic on first
// use" path. A host without that API (the standalone runtime cannot derive
// boundaries) simply reports them missing.

/** A country/state feature id, e.g. "IN" or "IN-MH". */
export type FeatureId = string

export interface GeoIndex {
  /** normalized country alias -> ISO A2 */
  countries: Record<string, string>
  /** ISO A2 -> (normalized state alias -> feature id) */
  states_by_country: Record<string, Record<string, string>>
  /** normalized state alias -> feature id, when unambiguous across the store */
  states_global: Record<string, string>
}

export interface FeatureCollection {
  type: string
  features: Array<{
    type: string
    properties: Record<string, unknown>
    geometry: unknown
  }>
}

export type GeoLevel = 'country' | 'state'

const GEO_BASE = '/geo'
const DOWNLOAD_API = '/api/geo/download'

/**
 * Normalize a lookup key EXACTLY as the Python builder does
 * (`geo_boundaries.norm`): NFKD-decompose, casefold, then keep only what
 * Python's `str.isalnum()` keeps — letters and numbers in ANY script.
 * "Tamil Nadu" / "TAMIL-NADU" / "tamilnadu" collapse to one key; so do
 * "Córdoba"/"Cordoba" (the accent decomposes into a combining mark, which is
 * neither a letter nor a number). Native-script names in the index — Hindi
 * "लद्दाख", Arabic, Cyrillic — must survive, so this canNOT filter to [a-z0-9]:
 * that would make every non-Latin alias unmatchable. test_geo_choropleth.js
 * asserts every key in the built index is a fixed point of this function.
 */
export function normalizeKey(value: unknown): string {
  return String(value ?? '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '')
}

// The index is small (a few hundred KB) and immutable — fetch once per page.
let indexPromise: Promise<GeoIndex | null> | null = null

export function loadGeoIndex(force = false): Promise<GeoIndex | null> {
  if (force) indexPromise = null
  if (!indexPromise) {
    indexPromise = fetch(`${GEO_BASE}/index.json`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => (d && typeof d === 'object' ? (d as GeoIndex) : null))
      .catch(() => null)
  }
  return indexPromise
}

export interface Detection {
  level: GeoLevel
  /** ISO A2 — only meaningful at state level. */
  country?: string
  /** How many distinct data values resolved to a feature id. */
  matched: number
  total: number
}

/**
 * Work out what the region column holds: country names/codes, or the states of
 * one country. `preferLevel`/`preferCountry` come from the visual's geoLevel /
 * geoCountry options; anything set there wins over detection.
 */
export function detectRegions(
  values: unknown[],
  index: GeoIndex | null,
  preferLevel: string = 'auto',
  preferCountry: string = '',
): Detection {
  const distinct = Array.from(new Set(values.map(normalizeKey).filter(Boolean)))
  const level = (preferLevel || 'auto').toLowerCase()
  const wantCountry = normalizeKey(preferCountry)
  const countryCode = wantCountry
    ? (index?.countries?.[wantCountry] ?? (preferCountry.trim().length === 2
        ? preferCountry.trim().toUpperCase() : undefined))
    : undefined

  if (!index) {
    // No index yet (nothing downloaded). Trust the options, else assume the
    // country level — its base layer is what a first download always writes.
    return { level: (level === 'state' ? 'state' : 'country') as GeoLevel,
             country: countryCode, matched: 0, total: distinct.length }
  }

  const countryHits = distinct.filter((v) => index.countries?.[v]).length
  if (level === 'country') return { level: 'country', matched: countryHits, total: distinct.length }

  // State level: the requested country, else the country whose state aliases
  // match the most values.
  const scoreFor = (cc: string): number => {
    const aliases = index.states_by_country?.[cc]
    if (!aliases) return 0
    return distinct.filter((v) => aliases[v]).length
  }
  let bestCc = countryCode
  let bestScore = bestCc ? scoreFor(bestCc) : 0
  if (!countryCode) {
    for (const cc of Object.keys(index.states_by_country ?? {})) {
      const s = scoreFor(cc)
      if (s > bestScore) { bestScore = s; bestCc = cc }
    }
  }

  if (level === 'state') {
    return { level: 'state', country: bestCc, matched: bestScore, total: distinct.length }
  }
  // auto — whichever interpretation explains more of the data. Ties go to
  // states: a column of country names is rarely also a set of state names.
  if (countryHits > bestScore) {
    return { level: 'country', matched: countryHits, total: distinct.length }
  }
  return { level: 'state', country: bestCc, matched: bestScore, total: distinct.length }
}

/** Resolve ONE data value to a feature id for the detected geography. */
export function resolveFeatureId(
  value: unknown,
  index: GeoIndex | null,
  detection: Detection,
): FeatureId | null {
  const key = normalizeKey(value)
  if (!key) return null
  if (detection.level === 'country') {
    const hit = index?.countries?.[key]
    if (hit) return hit
    return key.length === 2 ? key.toUpperCase() : null
  }
  const cc = detection.country
  const scoped = cc ? index?.states_by_country?.[cc] : undefined
  return scoped?.[key] ?? index?.states_global?.[key] ?? null
}

export class BoundariesUnavailable extends Error {
  readonly country?: string
  readonly canDownload: boolean
  constructor(message: string, country?: string, canDownload = false) {
    super(message)
    this.name = 'BoundariesUnavailable'
    this.country = country
    this.canDownload = canDownload
  }
}

function boundaryUrl(detection: Detection): string {
  return detection.level === 'country'
    ? `${GEO_BASE}/admin0/world.geojson`
    : `${GEO_BASE}/admin1/${(detection.country ?? '').toUpperCase()}.geojson`
}

async function fetchCollection(url: string): Promise<FeatureCollection | null> {
  let resp: Response
  try {
    resp = await fetch(url, { credentials: 'same-origin' })
  } catch {
    return null  // offline / blocked — treated as "not available"
  }
  if (!resp.ok) return null
  try {
    const data = await resp.json()
    return data && Array.isArray(data.features) ? (data as FeatureCollection) : null
  } catch {
    return null
  }
}

/**
 * Ask the backend to download one country and wait for it (bounded).
 * Returns true when the boundaries are on the server afterwards.
 */
async function requestDownload(
  country: string,
  onProgress?: (msg: string) => void,
  timeoutMs = 240_000,
): Promise<boolean> {
  let started: Response
  try {
    started = await fetch(DOWNLOAD_API, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ country }),
    })
  } catch {
    return false
  }
  if (!started.ok) return false
  let state: { status?: string; error?: string; messages?: string[] } = {}
  try {
    state = await started.json()
  } catch {
    return false
  }
  if (state.status === 'present') return true

  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 2000))
    let poll: Response
    try {
      poll = await fetch(`${DOWNLOAD_API}/${encodeURIComponent(country)}`,
                         { credentials: 'same-origin' })
    } catch {
      return false
    }
    if (!poll.ok) return false
    try {
      state = await poll.json()
    } catch {
      return false
    }
    const last = state.messages?.[state.messages.length - 1]
    if (last) onProgress?.(last)
    if (state.status === 'done' || state.status === 'present') return true
    if (state.status === 'error' || state.status === 'absent') return false
  }
  return false
}

/**
 * Boundary shapes for a detected geography, downloading them on first use.
 * Throws `BoundariesUnavailable` when they cannot be produced — the caller
 * turns that into an on-map message (and, in the designer, a Download button).
 */
export async function loadBoundaries(
  detection: Detection,
  onProgress?: (msg: string) => void,
): Promise<FeatureCollection> {
  if (detection.level === 'state' && !detection.country) {
    throw new BoundariesUnavailable(
      'Could not tell which country these regions belong to — set the visual’s ' +
      '“Country” option (e.g. IN) or use full region names.',
    )
  }
  const url = boundaryUrl(detection)
  const first = await fetchCollection(url)
  if (first) return first

  const country = detection.level === 'country' ? 'world' : (detection.country ?? '')
  onProgress?.(`Downloading map boundaries${country && country !== 'world' ? ` for ${country}` : ''}…`)
  const ok = await requestDownload(country === 'world' ? 'world' : country, onProgress)
  if (ok) {
    // The alias index gains this country's states — reload it so the join can
    // resolve them on the retry.
    await loadGeoIndex(true)
    const second = await fetchCollection(url)
    if (second) return second
  }
  throw new BoundariesUnavailable(
    detection.level === 'country'
      ? 'World map boundaries are not available on this server yet.'
      : `Map boundaries for ${detection.country} are not available on this server yet.`,
    detection.country,
    true,
  )
}
